/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Camera } from '@appCanvasCommon/core/Camera';
import { WorldContainer } from '@appCanvasCommon/core/WorldContainer';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { LayerManager } from '@appCanvasCommon/layers/LayerManager';
import { TweenManager } from '@appCanvasCommon/tween/TweenManager';
import { ICanvasApplicationInitOptions } from '@appCanvasCommon/interfaces/canvas-renderer.interface';
import { utils, autoDetectRenderer, IRenderer, Rectangle } from 'pixi.js-legacy';
import { ICanvasAngularDependencies } from 'src/app/app-data/interfaces/canvas-angular-land';
import { renderQueue } from 'src/app/plain-javascript/services/RenderQueue';
import { debounce, takeUntil, share } from 'rxjs/operators';
import { fromEvent, Subject, Observable } from 'rxjs';
import { drop } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { RendererInfo } from '@appCanvasCommon/core/RendererInfo';
import { EventMode } from '@appCanvasCommon/events/event-mode.enum';
import { IDestroyOptions } from '@appCanvasCommon/core/DestroyOptions';

export interface ICanvasApplication {
	initializeLayers: (initOptions: ICanvasApplicationInitOptions) => void;
	destroy: (thing?: any) => void;
	camera: Camera;
	world: WorldContainer;
	stage: FancyContainer;
	renderer: IRenderer;
	layerManager: LayerManager;
	tweenManager: TweenManager;
	viewport: Rectangle;
	backgroundGestureContainer: FancyContainer;
	canvasAngularLand: ICanvasAngularDependencies;
	onResize$: Observable<any>;
	rendererInfo: RendererInfo;
	update: (...things: any) => any;
	safeDraw: (...things: any) => any;
	draw: (...things: any) => any;
}

export interface ICanvasDrawingModule {
	draw: (...thing: any) => void;
}

export interface ICanvasUpdateModule {
	update: (...thing: any) => void;
}

export interface ICanvasDestroyable {
	destroy: (opts?: IDestroyOptions) => void;
}

export abstract class AbstractApplication
	extends utils.EventEmitter
	implements ICanvasApplication, ICanvasDrawingModule, ICanvasUpdateModule, ICanvasDestroyable
{
	public camera: Camera;
	public world: WorldContainer;
	public stage: FancyContainer;
	public view: HTMLCanvasElement;
	public renderer: IRenderer;
	public layerManager: LayerManager = new LayerManager();
	public tweenManager: TweenManager;
	public viewport: Rectangle;
	public backgroundGestureContainer: FancyContainer;
	public shutdownSubject$: Subject<boolean> = new Subject();
	public canvasAngularLand: ICanvasAngularDependencies;
	public onResize$: Observable<Event>;
	public rendererInfo: RendererInfo;
	protected initialOptions: ICanvasApplicationInitOptions;
	protected destroyList: any[] = [];
	protected isDirty = false;
	protected afterDraw$: any;
	protected _initalOptions: ICanvasApplicationInitOptions;

	protected constructor(
		angularDependencies: ICanvasAngularDependencies,
		initOptions: ICanvasApplicationInitOptions
	) {
		super();
		this._initalOptions = initOptions;
		this.canvasAngularLand = angularDependencies;
		this.initialOptions = initOptions;
		this.initializeRenderer(initOptions);
		this.initializeWorld(initOptions);
		this.initializeLayers(initOptions);

		const settle$: Observable<number> = renderQueue.ob$.pipe(drop(60), takeUntil(this.shutdownSubject$));

		this.afterDraw$ = fromEvent<any>(this.renderer, 'postrender').pipe(takeUntil(this.shutdownSubject$));

		this.afterDraw$.subscribe(() => {
			renderQueue.push(this.markAsPristine);
		});

		this.onResize$ = fromEvent(window, 'resize').pipe(
			debounce(() => settle$),
			takeUntil(this.shutdownSubject$)
		);

		window.textureCache = utils.TextureCache;
	}

	protected initializeRenderer(initOptions?: ICanvasApplicationInitOptions): void {
		this.renderer = autoDetectRenderer(initOptions);
		this.rendererInfo = new RendererInfo(this.renderer);
		this.stage = new FancyContainer();
		this.view = initOptions.view;
		this.tweenManager = new TweenManager(this);
		this.viewport = this.renderer.screen;

		this.stage.name = `root`;

		this.destroyList.push(this.renderer, this.stage, this.tweenManager, this.layerManager);
	}

	protected initializeWorld(initOptions?: ICanvasApplicationInitOptions): void {
		this.world = new WorldContainer();
		this.camera = new Camera(
			this.world,
			this.stage,
			this.tweenManager,
			this.world.x,
			this.world.y,
			this.viewport.width,
			this.viewport.height,
			this.canvasAngularLand
		);

		this.destroyList.push(this.camera, this.world);
	}

	protected initializeBackgroundGestures(): void {
		this.backgroundGestureContainer = new FancyContainer();
		(this.backgroundGestureContainer as any).imUnique = true;
		this.backgroundGestureContainer.hitArea = new Rectangle(
			0,
			0,
			this.renderer.view.width,
			this.renderer.view.height
		);
		this.backgroundGestureContainer.eventMode = EventMode.Static;

		this.layerManager.addChildToLayer(
			this._initalOptions.gestureContainerLayer,
			this.backgroundGestureContainer
		);

		this.destroyList.push(this.backgroundGestureContainer);
	}

	public initializeLayers(initOptions?: ICanvasApplicationInitOptions): void {
		if (this._initalOptions.shouldInitializeGestureContainer) {
			this.initializeBackgroundGestures();
		}
		// Override me to setup your world layers
		this.stage.addChild(this.world);
	}

	public update(delta?: number, forceDirty?: boolean): any {}

	public draw = () => {
		if (!this.renderer) {
			return;
		}
		this.renderer.render(this.stage);
		// this.markAsPristine();
	};

	public safeDraw = () => {
		if (!this.isDirty) {
			return;
		}
		renderQueue.push(this.draw, 'draw');
	};

	public markAsDirty = () => {
		if (this.isDirty) {
			return;
		}
		this.isDirty = true;
		this.safeDraw();
	};

	public markAsPristine = () => {
		this.isDirty = false;
	};

	public destroy(opts?: IDestroyOptions): void {
		this.shutdownSubject$.next(true);

		this.destroyList.forEach((thing) => thing.destroy(opts));

		this.camera = null;
		this.world = null;
		this.stage = null;
		this.view = null;
		this.renderer = null;
		this.layerManager = null;
		this.tweenManager = null;
		this.viewport = null;
		this.initialOptions = null;
		this.canvasAngularLand = null;
		this.destroyList = null;
		this.backgroundGestureContainer = null;
	}
}
